import is from '@sindresorhus/is';
import { useMemo } from 'react';

import type { NavigatableSidepanelItem } from './types';

type UseItemNavigationReturn = {
    readonly currentIndex: number;
    readonly goToNextItem: (() => void) | undefined;
    readonly goToPreviousItem: (() => void) | undefined;
};

export const useItemNavigation = (
    items: readonly NavigatableSidepanelItem[] | undefined,
    currentItemId: string,
    onNavigate: (itemId: string) => void,
    isEditing = false,
): UseItemNavigationReturn => {
    const currentIndex = useMemo(() => {
        if (is.undefined(items) || is.emptyArray(items)) {
            return -1;
        }

        return items.findIndex((item) => item.id === currentItemId);
    }, [items, currentItemId]);

    const goToPreviousItem = useMemo(() => {
        if (isEditing || is.undefined(items) || currentIndex <= 0) {
            return undefined;
        }

        return () => {
            const previousItem = items[currentIndex - 1];
            if (!is.undefined(previousItem)) {
                onNavigate(previousItem.id);
            }
        };
    }, [isEditing, items, currentIndex, onNavigate]);

    const goToNextItem = useMemo(() => {
        if (isEditing || is.undefined(items) || currentIndex < 0 || currentIndex >= items.length - 1) {
            return undefined;
        }

        return () => {
            const nextItem = items[currentIndex + 1];
            if (!is.undefined(nextItem)) {
                onNavigate(nextItem.id);
            }
        };
    }, [isEditing, items, currentIndex, onNavigate]);

    return {
        currentIndex,
        goToNextItem,
        goToPreviousItem,
    };
};
